import { useEffect, useRef, useState } from "react";
import { joinTranscriptChunks } from "@/lib/speech/speechDictationTranscript";
import { useManufacturingDictateSession } from "./useManufacturingDictateSession";

const idleMicClass =
  "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition disabled:cursor-not-allowed disabled:opacity-60 border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100 dark:border-violet-700 dark:bg-violet-950/40 dark:text-violet-200 dark:hover:bg-violet-900/40";
const recordingMicClass =
  "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition disabled:cursor-not-allowed disabled:opacity-60 border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-700 dark:bg-red-950/40 dark:text-red-200 dark:hover:bg-red-900/40";
const extractBtnClass =
  "rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60";
const textareaClass =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100 disabled:opacity-60";

export function ManufacturingDictatePad({
  onComplete,
  disabled,
  placeholder,
  extractLabel,
  resetKey,
}: {
  onComplete: (transcript: string) => void;
  disabled?: boolean;
  placeholder: string;
  extractLabel: string;
  /** Bump after a successful accept so the transcript box starts empty. */
  resetKey?: number;
}) {
  const session = useManufacturingDictateSession();
  const recording = session.state === "recording";
  const [text, setText] = useState("");
  const committedStopRef = useRef(false);

  useEffect(() => {
    setText("");
  }, [resetKey]);

  useEffect(() => {
    if (session.state === "recording") {
      committedStopRef.current = false;
      return;
    }
    if (session.state !== "stopped" || committedStopRef.current) return;
    committedStopRef.current = true;
    const captured = session.transcript;
    session.reset();
    if (captured.trim().length > 0) {
      setText((prev) => joinTranscriptChunks(prev, captured));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.state]);

  const trimmed = text.trim();
  const canExtract = !disabled && !recording && trimmed.length > 0;

  return (
    <div className="w-full space-y-2">
      <textarea
        rows={3}
        className={textareaClass}
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={Boolean(disabled)}
      />
      {recording ? (
        <div className="rounded-md border border-red-200 bg-red-50/70 px-3 py-2 text-[11px] text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          Listening — {session.transcript || "speak now…"}
        </div>
      ) : null}
      {session.error ? (
        <div className="text-[10px] text-red-700 dark:text-red-300">{session.error}</div>
      ) : null}
      <div className="flex items-center justify-end gap-2">
        {session.isSupported ? (
          <button
            type="button"
            onClick={() => (recording ? session.stop() : session.start())}
            disabled={Boolean(disabled)}
            aria-pressed={recording}
            className={recording ? recordingMicClass : idleMicClass}
          >
            {recording ? "Stop" : "Dictate"}
          </button>
        ) : (
          <span className="text-[10px] text-gray-500">Typing still works without voice.</span>
        )}
        <button
          type="button"
          className={extractBtnClass}
          disabled={!canExtract}
          onClick={() => {
            if (trimmed) onComplete(trimmed);
          }}
        >
          {extractLabel}
        </button>
      </div>
    </div>
  );
}
